import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Calendar, Copy, FileType, Loader2, Trash2, User, Users, X } from "lucide-react";
import { UserFilter } from "@/components/filters/UserFilter";
import { DocumentTypeIcon } from "@/components/knowledge-base/DocumentTypeIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAllAgents } from "@/hooks/agent";
import { usePaginatedDocuments } from "@/hooks/documents/useDocumentFetch";
import { useCloneDocument, useDeleteDocument } from "@/hooks/documents/useDocumentMutations";
import { useFolders } from "@/hooks/folders";
import { DocumentTypeIconMap, DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { formatFileSize } from "@/utils/formatting/general";
import { BulkUpdateDrawer } from "./BulkUpdateDrawer";
import { CloneDocumentModal } from "./CloneDocumentModal";

const getSourceTypeLabel = (sourceType: string | null | undefined): string => {
  if (!sourceType) return "";
  return (
    DocumentTypeIconMap[sourceType as keyof typeof DocumentTypeIconMap]?.label ||
    sourceType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

interface KnowledgeBaseListProps {
  onDocumentClick: (document: DocumentWithMetadata) => void;
}

export function KnowledgeBaseList({ onDocumentClick }: KnowledgeBaseListProps) {
  const { data: agentsData, isLoading: agentsLoading } = useAllAgents(500);
  const agents: any[] = (agentsData?.allAgents ?? []).slice().sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );
  const [selectedAgentId, setSelectedAgentId] = useState<string>("all");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedPrivacy, setSelectedPrivacy] = useState<string>("all");
  const [selectedActivityStatus, setSelectedActivityStatus] = useState<string>("all");
  const [selectedProcessingStatus, setSelectedProcessingStatus] = useState<string>("all");
  const [selectedSourceType, setSelectedSourceType] = useState<string>("all");
  const [selectedFolderId, setSelectedFolderId] = useState<string>("all");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [documentToClone, setDocumentToClone] = useState<DocumentWithMetadata | null>(null);
  const [cloningDocId, setCloningDocId] = useState<string | null>(null);
  const deleteDocument = useDeleteDocument();
  const cloneDocument = useCloneDocument();
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const { folders } = useFolders({ userId: selectedUserId || undefined });

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = usePaginatedDocuments(
    "all-documents",
    20,
    {
      searchTerm: searchTerm.trim() || undefined,
      agentId: selectedAgentId !== "all" ? selectedAgentId : undefined,
      selectedPrivacy: selectedPrivacy !== "all" ? selectedPrivacy : undefined,
      selectedActivityStatus: selectedActivityStatus !== "all" ? selectedActivityStatus : undefined,
      selectedProcessingStatus:
        selectedProcessingStatus !== "all" ? selectedProcessingStatus : undefined,
      selectedSourceType: selectedSourceType !== "all" ? selectedSourceType : undefined,
      folderId: selectedFolderId !== "all" ? selectedFolderId : undefined,
    },
    selectedUserId || undefined,
  );

  const documents = data?.pages?.flatMap((page) => page.data) ?? [];
  const totalDocuments = data?.pages?.[0]?.total ?? documents.length;

  const { data: allDocsData } = usePaginatedDocuments(
    "all-documents",
    500,
    {},
    selectedUserId || undefined,
  );
  const allDocuments = allDocsData?.pages?.flatMap((page) => page.data) ?? [];

  const sourceTypes = Array.from(
    new Set(allDocuments.map((doc) => doc.source_type).filter(Boolean)),
  );
  const processingStatuses = Array.from(
    new Set(allDocuments.map((doc) => doc.status).filter(Boolean)),
  );

  const totalFileSize = documents.reduce((sum, doc) => sum + (doc.metadata?.pdf_size || 0), 0);
  const publicCount = documents.filter((doc) => doc.is_public).length;
  const privateCount = documents.filter((doc) => !doc.is_public).length;
  const activeCount = documents.filter((doc) => doc.is_active).length;
  const totalAgentAssociations = documents.reduce(
    (sum, doc) => sum + (doc.dependent_agents?.length || 0),
    0,
  );

  const hasActiveFilters =
    searchTerm ||
    selectedAgentId !== "all" ||
    selectedPrivacy !== "all" ||
    selectedActivityStatus !== "all" ||
    selectedProcessingStatus !== "all" ||
    selectedSourceType !== "all" ||
    selectedFolderId !== "all" ||
    selectedUserId !== null;

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedAgentId("all");
    setSelectedPrivacy("all");
    setSelectedActivityStatus("all");
    setSelectedProcessingStatus("all");
    setSelectedSourceType("all");
    setSelectedFolderId("all");
    setSelectedUserId(null);
  };

  const toggleDocumentSelection = (docId: string) => {
    const newSelected = new Set(selectedDocumentIds);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocumentIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedDocumentIds.size === documents.length) {
      setSelectedDocumentIds(new Set());
    } else {
      setSelectedDocumentIds(new Set(documents.map((doc) => doc.id)));
    }
  };

  const selectedDocuments = documents.filter((doc) => selectedDocumentIds.has(doc.id));

  const handleCloneClick = (doc: DocumentWithMetadata) => {
    setCloningDocId(doc.id);
    setDocumentToClone(doc);
    setIsCloneModalOpen(true);
  };

  const handleCloneConfirm = (name: string, userId: string) => {
    if (documentToClone) {
      cloneDocument.mutate(
        { documentId: documentToClone.id, name, userId },
        {
          onSettled: () => setCloningDocId(null),
        },
      );
    }
  };

  return (
    <div className="space-y-6">
      <BulkUpdateDrawer
        isOpen={isBulkUpdateOpen}
        onClose={() => {
          setIsBulkUpdateOpen(false);
          setSelectedDocumentIds(new Set());
        }}
        selectedDocuments={selectedDocuments}
      />
      <CloneDocumentModal
        isOpen={isCloneModalOpen}
        onClose={() => {
          setIsCloneModalOpen(false);
          setDocumentToClone(null);
        }}
        document={documentToClone}
        onConfirm={handleCloneConfirm}
        isLoading={cloneDocument.isPending}
      />

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Knowledge Base Documents</h3>
          <div className="flex items-center gap-2">
            {selectedDocumentIds.size > 0 && (
              <Button onClick={() => setIsBulkUpdateOpen(true)} variant="default" size="sm">
                Update {selectedDocumentIds.size} Document(s)
              </Button>
            )}
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <X className="h-4 w-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex-1 min-w-0">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search documents..."
              className="w-full"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={selectedPrivacy} onValueChange={setSelectedPrivacy}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by privacy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Privacy</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={selectedActivityStatus} onValueChange={setSelectedActivityStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activity</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <UserFilter
                selectedUserId={selectedUserId}
                onUserChange={setSelectedUserId}
                placeholder="All Users"
                showLabel={false}
              />
            </div>
            {folders.length > 0 && (
              <div>
                <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Folders</SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {processingStatuses.length > 0 && (
              <div>
                <Select
                  value={selectedProcessingStatus}
                  onValueChange={setSelectedProcessingStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by processing status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Processing Status</SelectItem>
                    {processingStatuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {sourceTypes.length > 0 && (
              <div>
                <Select value={selectedSourceType} onValueChange={setSelectedSourceType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {sourceTypes.map((sourceType) => (
                      <SelectItem key={sourceType} value={sourceType!}>
                        {getSourceTypeLabel(sourceType)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasActiveFilters && documents.length > 0 && (
        <div className="bg-genie-primary/5 border border-genie-primary/20 rounded-xl p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
            {[
              { label: "Documents", value: documents.length, className: "text-gray-900" },
              { label: "Total Size", value: formatFileSize(totalFileSize), className: "text-gray-900" },
              { label: "Public", value: publicCount, className: "text-genie-primary" },
              { label: "Private", value: privateCount, className: "text-gray-700" },
              { label: "Active", value: activeCount, className: "text-genie-primary" },
              { label: "Agent Links", value: totalAgentAssociations, className: "text-genie-primary" },
            ].map(({ label, value, className }) => (
              <div key={label} className="flex flex-col">
                <span className="text-gray-500 text-xs font-medium">{label}</span>
                <span className={`text-lg font-semibold ${className}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No documents found</div>
      ) : (
        <>
          {documents.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-genie-border">
              <Checkbox
                checked={selectedDocumentIds.size === documents.length && documents.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <span className="text-sm text-gray-600">
                {selectedDocumentIds.size > 0
                  ? `${selectedDocumentIds.size} of ${documents.length} selected`
                  : `Select all ${documents.length} documents`}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {documents.map((doc) => {
              const isSelected = selectedDocumentIds.has(doc.id);
              return (
                <div
                  key={doc.id}
                  className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
                >
                  <div className={`group rounded-[10px] h-full flex flex-col overflow-hidden ${isSelected ? "bg-genie-primary/10" : "bg-white"}`}>
                    {/* Card header */}
                    <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleDocumentSelection(doc.id)}
                          />
                        </div>
                        <DocumentTypeIcon document={doc} showTooltip={true} iconSize="md" />
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                            doc.is_public
                              ? "bg-genie-primary/10 text-genie-primary"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {doc.is_public ? "Public" : "Private"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          className="text-gray-400 hover:text-genie-primary transition-colors disabled:opacity-50"
                          onClick={(e) => { e.stopPropagation(); handleCloneClick(doc); }}
                          disabled={cloneDocument.isPending}
                          title="Clone document"
                        >
                          {cloningDocId === doc.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Copy className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingDocId(doc.id);
                            deleteDocument.mutate(doc.id, { onSettled: () => setDeletingDocId(null) });
                          }}
                          disabled={deletingDocId === doc.id || deleteDocument.isPending}
                          title={doc.el_doc_id ? "Remove from knowledge base" : "Delete document"}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Clickable body */}
                    <div onClick={() => onDocumentClick(doc)} className="cursor-pointer flex flex-col flex-1 px-4 pt-3 pb-4 gap-3">
                      <h4
                        className="text-sm font-semibold text-gray-900 group-hover:text-genie-primary transition-colors line-clamp-2 leading-snug"
                        title={doc.name || "Untitled Document"}
                      >
                        {doc.name || "Untitled Document"}
                      </h4>

                      <div className="text-xs space-y-1.5 text-gray-500 flex-1">
                        {doc.source_type && (
                          <div className="flex items-center gap-1.5">
                            <FileType className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span>{getSourceTypeLabel(doc.source_type)}</span>
                          </div>
                        )}
                        {doc.created_at && (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <CopyableEmail email={doc.owner?.full_name || doc.owner?.email} fallback="Unknown" textClassName="truncate" />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <span>
                            <span className="font-medium text-genie-primary">{doc.dependent_agents?.length || 0}</span> agent{doc.dependent_agents?.length !== 1 ? "s" : ""}
                          </span>
                          {doc.metadata?.pdf_size && (
                            <span className="ml-auto text-gray-400">{formatFileSize(doc.metadata.pdf_size)}</span>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-gray-400 font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100 truncate">
                        {doc.id}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Load More Button */}
      {hasNextPage && !isLoading && documents.length > 0 && (
        <div className="flex justify-center pt-6">
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="outline"
            className="px-6"
          >
            {isFetchingNextPage ? "Loading..." : "Load More Documents"}
          </Button>
        </div>
      )}
    </div>
  );
}
